import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import MonthNavigator from '../../../components/MonthNavigator';
import { useToast } from '../../../components/Toast';
import { nowKST } from '../../../utils/date';
import { monthLabel, numFmt, PAYOUT_LABELS } from './IncentiveRuleModal.constants';
import IncentiveRuleModal from './IncentiveRuleModal';
import IncentiveUserDetail from './IncentiveUserDetail';
import {
  stdPageTitle, stdPageSubtitle, stdHeaderRow, stdBlackBtn, stdOutlineBtn,
  stdTabGroup, stdTabBtn, stdBadgeActive, stdBadgeMuted,
} from '../adminlayout/designStandard';

const card = {
  background: 'var(--bg-card, #fff)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 16,
  marginBottom: 12,
  transition: 'box-shadow .15s',
};

export default function IncentiveManagement({ companyCode }) {
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [records, setRecords] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const now = nowKST();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [defaultMonthReady, setDefaultMonthReady] = useState(false);
  const userPickedMonthRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const loadRules = useCallback(async () => {
    if (!companyCode || !defaultMonthReady) return;
    const res = await apiClient.get(`/api/admin/incentive/rules?company_code=${companyCode}&year=${year}&month=${month}`);
    if (res.ok) setRules(res.data?.data || []);
  }, [companyCode, defaultMonthReady, year, month]);

  const loadRecords = useCallback(async () => {
    if (!companyCode || !defaultMonthReady) return;
    const res = await apiClient.get(`/api/admin/incentive/records?company_code=${companyCode}&year=${year}&month=${month}`);
    if (res.ok) setRecords(res.data?.data || []);
  }, [companyCode, defaultMonthReady, year, month]);

  const loadTicketTypes = useCallback(async () => {
    const res = await apiClient.get('/api/admin/incentive/ticket-types');
    if (res.ok) setTicketTypes(res.data?.data || []);
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadTicketTypes(); }, [loadTicketTypes]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let alive = true;
    userPickedMonthRef.current = false;
    setDefaultMonthReady(false);

    async function applyDefaultMonth() {
      const res = await apiClient.get(`/api/admin/incentive/default-month?company_code=${companyCode}`);
      if (alive && res.ok && res.data?.data && !userPickedMonthRef.current) {
        setYear(Number(res.data.data.year));
        setMonth(Number(res.data.data.month));
      }
      if (alive) setDefaultMonthReady(true);
    }

    if (companyCode) applyDefaultMonth();
    else setDefaultMonthReady(true);
    return () => { alive = false; };
  }, [companyCode]);

  function handleMonthChange(next) {
    userPickedMonthRef.current = true;
    setYear(Number(next.year));
    setMonth(Number(next.month));
  }

  async function handleDefaultMonth() {
    if (!companyCode) return;
    const res = await apiClient.get(`/api/admin/incentive/default-month?company_code=${companyCode}`);
    if (!res.ok || !res.data?.data) return;
    userPickedMonthRef.current = false;
    setYear(Number(res.data.data.year));
    setMonth(Number(res.data.data.month));
  }

  async function handleDelete(id) {
    if (!window.confirm('이 규칙을 삭제할까요?\n월별 실적이 있는 규칙은 삭제할 수 없습니다.')) return;
    const res = await apiClient.del(`/api/admin/incentive/rules/${id}`);
    if (res.ok) {
      showToast('삭제 완료', 'success');
      loadRules();
    } else {
      showToast(res.data?.message || '삭제 실패', 'error');
    }
  }

  function handleModalSave() {
    setModalOpen(false);
    setEditRule(null);
    loadRules();
    loadRecords();
  }

  const getRuleRecords = (rule) => records.filter((record) => (
    Number(record.rule_id) === Number(rule.id) || record.rule_name === rule.name
  ));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>인센티브 관리</h2>
          <div style={stdPageSubtitle}>인센티브 기준·지급 내역을 관리합니다.</div>
        </div>
        {tab === 'rules' && (
          <button style={stdBlackBtn} onClick={() => { setEditRule(null); setModalOpen(true); }}>
            + 규칙 추가
          </button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={stdTabGroup}>
          <button style={stdTabBtn(tab === 'rules')} onClick={() => setTab('rules')}>규칙 관리</button>
          <button style={stdTabBtn(tab === 'users')} onClick={() => setTab('users')}>직원별 내역</button>
        </div>
      </div>

      {tab === 'users' && <IncentiveUserDetail companyCode={companyCode} />}

      {tab === 'rules' && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <MonthNavigator
              year={year}
              month={month}
              onChange={handleMonthChange}
              onReset={handleDefaultMonth}
              showReset
              disabled={!defaultMonthReady}
              compact
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              선택한 월에 적용되는 인센티브 기준과 실적을 봅니다.
            </span>
          </div>

          {rules.length === 0 && (
            <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>
              등록된 인센티브 규칙이 없습니다.
            </div>
          )}

          {rules.map((rule) => {
            const pm = rule.payout_method || {};
            const isExpanded = expanded === rule.id;
            const ruleRecords = getRuleRecords(rule);
            return (
              <div key={rule.id} style={{ ...card, borderLeft: `4px solid ${rule.is_active ? 'var(--std-accent, #1d7de6)' : '#999'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>{rule.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{rule.description || ''}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={stdBadgeActive}>{PAYOUT_LABELS[pm.type] || pm.type}</span>
                      {pm.threshold != null && <span style={stdBadgeActive}>기준: {numFmt(pm.threshold)}점</span>}
                      {pm.unit_price != null && <span style={stdBadgeActive}>단가: {numFmt(pm.unit_price)}원</span>}
                      <span style={stdBadgeActive}>배정 {rule.assign_count}명</span>
                      {rule.effective_from_month && <span style={stdBadgeActive}>{monthLabel(rule.effective_from_month)}</span>}
                      {(rule.version_timeline || []).length > 1 && <span style={stdBadgeActive}>기준 이력 {(rule.version_timeline || []).length}개</span>}
                      {!rule.is_active && <span style={stdBadgeMuted}>비활성</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button style={stdOutlineBtn} onClick={() => { setEditRule(rule); setModalOpen(true); }}>수정</button>
                    <button style={{ ...stdOutlineBtn, color: '#ef4444' }} onClick={() => handleDelete(rule.id)}>삭제</button>
                  </div>
                </div>

                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={() => setExpanded(isExpanded ? null : rule.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--std-accent-text, #1d7de6)', fontWeight: 700, fontSize: 12 }}
                  >
                    {isExpanded ? '접기' : `상세 보기 (${ruleRecords.length}건)`}
                  </button>

                  {isExpanded && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, color: 'var(--text-muted)' }}>배정 직원</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                        {(rule.assigned_users || []).map((user) => (
                          <span key={user.id} style={{ ...stdBadgeActive, fontSize: 11 }}>{user.name}</span>
                        ))}
                        {(!rule.assigned_users || rule.assigned_users.length === 0) && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>배정된 직원 없음</span>
                        )}
                      </div>

                      {ruleRecords.length > 0 ? (
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                              <th style={{ textAlign: 'left', padding: 4 }}>직원</th>
                              <th style={{ textAlign: 'right', padding: 4 }}>점수</th>
                              <th style={{ textAlign: 'center', padding: 4 }}>조건</th>
                              <th style={{ textAlign: 'right', padding: 4 }}>금액</th>
                              <th style={{ textAlign: 'center', padding: 4 }}>수동</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ruleRecords.map((record) => (
                              <tr key={record.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: 4 }}>{record.user_name}</td>
                                <td style={{ textAlign: 'right', padding: 4 }}>{numFmt(record.raw_score)}</td>
                                <td style={{ textAlign: 'center', padding: 4 }}>{record.condition_met ? '충족' : '미달'}</td>
                                <td style={{ textAlign: 'right', padding: 4, fontWeight: 700 }}>{numFmt(record.calculated_amount)}원</td>
                                <td style={{ textAlign: 'center', padding: 4 }}>{record.is_manual_override ? '수동' : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{year}년 {month}월 실적 없음</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {modalOpen && (
            <IncentiveRuleModal
              rule={editRule}
              companyCode={companyCode}
              ticketTypes={ticketTypes}
              year={year}
              month={month}
              onSave={handleModalSave}
              onClose={() => { setModalOpen(false); setEditRule(null); }}
            />
          )}
        </>
      )}
    </div>
  );
}
